import React, { useRef, useState } from "react";
import { useTranslation } from "../../../../../i18n/LanguageContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faInfoCircle,
  faChartBar,
  faExclamationTriangle,
  faCheckCircle,
  faEnvelope,
  faPaperclip,
  faFileAlt,
  faFilePdf,
  faFileImage,
  faFileArchive,
  faFileCode,
  faSearch,
  faChevronUp,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";
import {
  decodeQuotedPrintable,
  formatDate,
  decodeAndSanitizeHtml,
} from "../utils/emailAnalysisUtils";
import OreInsightCard from "../../common/OreInsightCard";
import ThreatBannerShareButton from "../../common/ThreatBannerShareButton";
import SuggestionsBulletList from "../../common/SuggestionsBulletList";
import ReanalyzeWithContextPanel from "../../common/ReanalyzeWithContextPanel";
import AnalysisFeedbackBar from "../../common/AnalysisFeedbackBar";
import EmptyStatePanel from "../../common/EmptyStatePanel";

const AnalysisDetailsSimple = ({
  selectedAnalysis,
  onScrollToAnalysisResults,
  reportId,
  onReanalyzeWithContext,
  readOnly = false,
}) => {
  const reanalyzeRef = useRef(null);
  const { t } = useTranslation();

  const scrollToReanalyze = () => {
    if (reanalyzeRef.current) {
      reanalyzeRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };
  if (!selectedAnalysis) {
    return (
      <>
        <div className="common-section-header">
          <h3>
            <FontAwesomeIcon icon={faChartBar} />
            {t("email.section.analysisDetails")}
          </h3>
          <p>{t("email.section.analysisDetailsSubtitle")}</p>
        </div>
        <div className="panel-content details-panel-content">
          <EmptyStatePanel
            icon={faEnvelope}
            title={t("email.section.noAnalysisSelected")}
            description={t("email.section.noAnalysisSelectedDesc")}
            className="no-analysis-selected"
          />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="common-section-header">
        <h3>
          <FontAwesomeIcon icon={faChartBar} />
          {t("email.section.analysisDetails")}
        </h3>
        <p>{t("email.section.analysisDetailsSubtitle")}</p>
      </div>
      <div className="panel-content details-panel-content">
        <AnalysisSummarySimple
          selectedAnalysis={selectedAnalysis}
          formatDate={formatDate}
          decodeQuotedPrintable={decodeQuotedPrintable}
          reportId={reportId}
        />

        <AnalysisSuggestions selectedAnalysis={selectedAnalysis} />

        <RiskFactors selectedAnalysis={selectedAnalysis} />

        <AnalysisReasoning selectedAnalysis={selectedAnalysis} />

        {!readOnly && (
          <AnalysisFeedbackBar
            analysisKey={selectedAnalysis?.key}
            onNegativeFeedback={scrollToReanalyze}
          />
        )}

        {!readOnly && (
          <div ref={reanalyzeRef}>
            <ReanalyzeWithContextPanel
              initialContext={
                selectedAnalysis?.analysis?.analysisResult?.htmlEmailContent
                  ?.userContext ||
                selectedAnalysis?.analysis?.analysisResult?.analyzedEmailContent
                  ?.userContext ||
                ""
              }
              onSubmit={(context) =>
                onReanalyzeWithContext?.(selectedAnalysis, context)
              }
            />
          </div>
        )}

        <OreInsightCard
          oreData={selectedAnalysis.analysis?.analysisResult}
          channel="email"
        />

        <AttachmentInfo selectedAnalysis={selectedAnalysis} />
      </div>
    </>
  );
};

const AnalysisSummarySimple = ({
  selectedAnalysis,
  formatDate,
  decodeQuotedPrintable,
  reportId,
}) => {
  const { t } = useTranslation();
  // Helper function to get threat banner class
  const getThreatBannerClass = () => {
    const isScam = selectedAnalysis.analysis?.analysisResult?.isScam;
    const confidence =
      selectedAnalysis.analysis?.analysisResult?.confidence || 0;

    // If the analysis explicitly marks the email as not a scam, treat it as safe
    if (isScam === false) {
      return "safe";
    }

    // If explicitly a scam, show threat
    if (isScam === true) {
      return "threat-detected";
    }

    // If we don't have a clear verdict, but confidence is very low, mark as security-failed
    if (confidence <= 0.2) {
      return "security-failed";
    }

    return "safe";
  };

  // Helper function to get threat title text
  const getThreatTitle = () => {
    const isScam = selectedAnalysis.analysis?.analysisResult?.isScam;
    return isScam
      ? t("email.detail.scamDetected")
      : t("email.detail.noThreats");
  };

  // Helper function to get status icon
  const getStatusIcon = () => {
    const isScam = selectedAnalysis.analysis?.analysisResult?.isScam;
    return isScam ? faExclamationTriangle : faCheckCircle;
  };

  return (
    <div className="case-details">
      {/* Threat Assessment Banner */}
      <div className={`threat-banner ${getThreatBannerClass()}`}>
        <div className="threat-icon">
          <FontAwesomeIcon icon={getStatusIcon()} />
        </div>
        <div className="threat-content">
          <h2 className="threat-title">{getThreatTitle()}</h2>
          <p className="threat-subtitle">
            {selectedAnalysis.analysis?.analysisResult?.isScam
              ? t("email.detail.scamSubtitle")
              : t("email.detail.safeSubtitle")}
          </p>
        </div>
        <ThreatBannerShareButton
          scamDetected={
            selectedAnalysis.analysis?.analysisResult?.isScam === true
          }
          threatType={t("email.share.threatType")}
          threatTitle={t("email.share.threatTitle")}
          threatDetail={t("email.detail.scamSubtitle")}
          reportId={reportId}
        />
      </div>
    </div>
  );
};

const AnalysisSuggestions = ({ selectedAnalysis }) => {
  const { t } = useTranslation();
  // Primary suggestions field from backend
  let suggestions = selectedAnalysis?.analysis?.analysisResult?.suggestions;

  // Fallback: try to extract recommendations from the reasoning/explanation text
  const explanation =
    selectedAnalysis?.analysis?.analysisResult?.reasoning ||
    selectedAnalysis?.analysis?.analysisResult?.explanation ||
    "";

  const extractRecommendations = (explanationText, fallback) => {
    if (!explanationText) return fallback || null;

    const patterns = [
      /###\s*(?:Safety\s+)?Recommendations?\s*\n([\s\S]*?)(?=\n###|\n\n###|$)/i,
      /###\s*Suggestions?\s*\n([\s\S]*?)(?=\n###|\n\n###|$)/i,
      /\*\*(?:Safety\s+)?Recommendations?\*\*\s*\n([\s\S]*?)(?=\n\*\*|\n\n\*\*|$)/i,
    ];

    for (const pattern of patterns) {
      const match = explanationText.match(pattern);
      if (match && match[1]) {
        return match[1].trim().replace(/\n{3,}/g, "\n\n");
      }
    }

    return fallback || null;
  };

  if (!suggestions) {
    suggestions = extractRecommendations(explanation, null);
  }

  if (!suggestions) return null;

  return (
    <div className="suggestions-section">
      <h4 className="subsection-title">
        <FontAwesomeIcon
          icon={faInfoCircle}
          className="fa-icon-margin-right-8"
        />
        {t("email.section.whatToDo")}
      </h4>
      <SuggestionsBulletList text={suggestions} />
    </div>
  );
};

const AttachmentInfo = ({ selectedAnalysis }) => {
  const { t } = useTranslation();
  if (
    !selectedAnalysis?.analysisResult?.analyzedEmailContent?.attachmentNames ||
    selectedAnalysis.analysisResult.analyzedEmailContent.attachmentNames
      .length === 0
  ) {
    return null;
  }

  const attachments =
    selectedAnalysis.analysisResult.analyzedEmailContent.attachmentNames;

  // Helper function to get appropriate icon for file type
  const getFileIcon = (filename) => {
    const extension = filename.toLowerCase().split(".").pop();

    switch (extension) {
      case "pdf":
        return faFilePdf;
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
      case "bmp":
        return faFileImage;
      case "zip":
      case "rar":
      case "7z":
      case "tar":
      case "gz":
        return faFileArchive;
      case "exe":
      case "bat":
      case "com":
      case "scr":
      case "cmd":
        return faFileCode;
      default:
        return faFileAlt;
    }
  };

  // Helper function to get risk level for file type
  const getAttachmentRiskLevel = (filename) => {
    const extension = filename.toLowerCase().split(".").pop();
    const highRiskExtensions = [
      "exe",
      "scr",
      "bat",
      "com",
      "cmd",
      "pif",
      "jar",
    ];
    const mediumRiskExtensions = [
      "zip",
      "rar",
      "7z",
      "doc",
      "docx",
      "xls",
      "xlsx",
      "ppt",
      "pptx",
    ];

    if (highRiskExtensions.includes(extension)) {
      return "high";
    } else if (mediumRiskExtensions.includes(extension)) {
      return "medium";
    }
    return "low";
  };

  return (
    <div className="common-form-group">
      <h4>
        <FontAwesomeIcon
          icon={faPaperclip}
          className="fa-icon-margin-right-8"
        />
        {t("email.section.emailAttachments")} ({attachments.length})
      </h4>
      <div className="attachment-list">
        {attachments.map((attachment, index) => {
          const riskLevel = getAttachmentRiskLevel(attachment);
          const fileIcon = getFileIcon(attachment);

          return (
            <div
              key={index}
              className={`attachment-item attachment-risk-${riskLevel}`}
            >
              <div className="attachment-icon">
                <FontAwesomeIcon icon={fileIcon} />
              </div>
              <div className="attachment-details">
                <div className="attachment-name">{attachment}</div>
                <div className="attachment-risk">
                  {riskLevel === "high" && (
                    <span className="risk-high">
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                      {t("email.attachment.highRisk")}
                    </span>
                  )}
                  {riskLevel === "medium" && (
                    <span className="risk-medium">
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                      {t("email.attachment.mediumRisk")}
                    </span>
                  )}
                  {riskLevel === "low" && (
                    <span className="risk-low">
                      <FontAwesomeIcon icon={faCheckCircle} />
                      {t("email.attachment.lowRisk")}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="attachment-warning">
        <FontAwesomeIcon
          icon={faExclamationTriangle}
          className="fa-icon-margin-right-8"
        />
        <strong>{t("email.attachment.warningLabel")}</strong>{" "}
        {t("email.attachment.warningText")}
      </div>
    </div>
  );
};

const RiskFactors = ({ selectedAnalysis }) => {
  const { t } = useTranslation();
  const suspiciousElements =
    selectedAnalysis.analysis?.analysisResult?.suspiciousElements;

  if (!suspiciousElements || suspiciousElements.length === 0) {
    return null;
  }

  return (
    <div className="risk-factors-section">
      <h4 className="subsection-title">
        <FontAwesomeIcon
          icon={faExclamationTriangle}
          className="fa-icon-margin-right-8"
        />
        {t("email.section.riskFactors")} ({suspiciousElements.length})
      </h4>
      <ul className="risk-factors-list">
        {suspiciousElements.map((factor, index) => (
          <li key={index}>
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className="risk-icon"
            />
            {factor}
          </li>
        ))}
      </ul>
    </div>
  );
};

const AnalysisReasoning = ({ selectedAnalysis }) => {
  const [explanationCollapsed, setExplanationCollapsed] = useState(false);
  const { t } = useTranslation();

  if (!selectedAnalysis.analysis?.analysisResult?.reasoning) {
    return null;
  }

  return (
    <div className="explanation-section">
      <h4
        className="subsection-title collapsible-header"
        onClick={() => setExplanationCollapsed(!explanationCollapsed)}
        style={{ cursor: "pointer" }}
      >
        <FontAwesomeIcon icon={faSearch} className="fa-icon-margin-right-8" />
        {t("email.section.explanation")}
        <FontAwesomeIcon
          icon={explanationCollapsed ? faChevronDown : faChevronUp}
          style={{ marginLeft: "auto", fontSize: "0.85rem" }}
        />
      </h4>
      {!explanationCollapsed && (
        <div
          className="explanation-text"
          dangerouslySetInnerHTML={{
            __html: decodeAndSanitizeHtml(
              selectedAnalysis.analysis.analysisResult.reasoning || "",
            ),
          }}
        />
      )}
    </div>
  );
};

export default AnalysisDetailsSimple;
